import { PointOfInterest } from '@/__generated__/graphql';
import * as analysisState from '@/components/Analysis/state';
import { PointOfInterestMarkerLabel } from '@/components/shared/PointOfInterestMarkerLabel';
import { point3dToNumber3 } from '@/utils/pointOfInterest';
import { checkIfVoi } from '@/utils/poi';
import { HtmlMarkerInstanceProps, HtmlMarkerSetProps, Number3 } from '@fabric-web/3d-viewer';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import * as poiState from '../state';

const DISTANCE_THRESHOLD = 250;
const MAX_MARKERS_TO_SHOW = 1000;
const MIN_STEP = 1;

export const usePointOfInterestMarkerLabels = () => {
  const selectedAssemblyId = useAtomValue(analysisState.selectedAssemblyId);
  const poiFilteredByCheckbox = useAtomValue(poiState.poiFilteredByCheckbox);
  const selectedPoiId = useAtomValue(poiState.selectedPointOfInterestId);
  const blisterToAdd = useAtomValue(poiState.blisterToAdd);
  const editPointOfInterest = useAtomValue(poiState.editPointOfInterest);
  const arePoisVisible = useAtomValue(analysisState.arePoisVisible);
  const showOnlySelectedPoi = useAtomValue(analysisState.showOnlySelectedPoi);
  const showPointOfInterestMarkerLabels = useAtomValue(
    analysisState.showPointOfInterestMarkerLabels
  );
  const allVoiTemplates = useAtomValue(analysisState.allVoiTemplates);
  const markerLabelScale = useAtomValue(analysisState.markerLabelScale);
  const markerLabelHeightVariance = useAtomValue(analysisState.markerLabelHeightVariance);
  const selectedSpherical = useAtomValue(analysisState.selectedSpherical);
  const swivelMarkerLabels = useAtomValue(analysisState.swivelMarkerLabels);

  const poiToExclude = useMemo(() => {
    if (blisterToAdd?.state !== 'UpdatingBlister' && !editPointOfInterest.isAutoGenerated)
      return undefined;
    return editPointOfInterest.pointOfInterest?.id;
  }, [blisterToAdd, editPointOfInterest]);

  const filteredPois = useMemo(() => {
    if (!arePoisVisible) return [];

    if (showOnlySelectedPoi && selectedPoiId) {
      return poiFilteredByCheckbox.filter((poi) => poi.id === selectedPoiId);
    }

    if (selectedAssemblyId) {
      return poiFilteredByCheckbox.filter(
        (poi) => poi.assembly.id === selectedAssemblyId && poi.id !== poiToExclude
      );
    }
    return poiToExclude
      ? poiFilteredByCheckbox.filter((poi) => poi.id !== poiToExclude)
      : poiFilteredByCheckbox;
  }, [
    arePoisVisible,
    showOnlySelectedPoi,
    poiFilteredByCheckbox,
    selectedAssemblyId,
    poiToExclude,
    selectedPoiId,
  ]);

  const poiMarkerLabelSet: HtmlMarkerSetProps[] = useMemo(() => {
    if (!showPointOfInterestMarkerLabels) return [];

    const instances: HtmlMarkerInstanceProps[] = [];

    filteredPois.forEach((poi) => {
      const step = Math.random() * (markerLabelHeightVariance - MIN_STEP) + MIN_STEP;
      const height = step * markerLabelScale;

      let position: Number3;
      const isVoi = checkIfVoi(allVoiTemplates, poi.type);

      if (isVoi && poi.cuboids && poi.cuboids.length > 0) {
        const cuboid = poi.cuboids[0];
        position = [
          cuboid.position[0],
          cuboid.position[1],
          cuboid.position[2] + cuboid.scale[2] / 2 - 0.05,
        ];
      } else {
        position = point3dToNumber3(poi.centerPoint3d, 0) as Number3;
      }

      instances.push({
        id: `html-marker-${poi.id}`,
        position,
        children: (
          <PointOfInterestMarkerLabel
            pointOfInterest={poi as PointOfInterest}
            height={height}
            scale={markerLabelScale}
            swivel={swivelMarkerLabels}
            selectedSpherical={!!selectedSpherical}
            selected={selectedPoiId === poi.id}
          />
        ),
        distanceThreshold: DISTANCE_THRESHOLD,
        scale: selectedSpherical ? 0.17 : 0.25,
        divStyle: {
          pointerEvents: 'none',
          backgroundColor: 'transparent',
        },
        markerHeight: selectedPoiId === poi.id ? 1 : 0.85,
      });
    });

    const markerSet: HtmlMarkerSetProps = {
      id: 'html-markerset-0',
      instances: instances.filter((instance) => !!instance.position),
      enableFilteringByVisibilityBox: true,
      maxMarkersToShow: MAX_MARKERS_TO_SHOW,
    };
    return [markerSet];
  }, [
    showPointOfInterestMarkerLabels,
    filteredPois,
    markerLabelHeightVariance,
    markerLabelScale,
    swivelMarkerLabels,
    selectedSpherical,
    selectedPoiId,
    allVoiTemplates,
  ]);

  return poiMarkerLabelSet;
};
